import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FilePdf, Anchor } from "@phosphor-icons/react";

const REPORT_TYPE_LABELS = {
  lashing: "Cargo Securing Survey Report", labeling: "Labeling Report",
  label_removal: "Label Removal Report", damaged_shipment: "Damaged Shipment Report",
  transloading: "Transloading Photo Report",
};

function FieldRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-1 py-2 border-b border-slate-100">
      <dt className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</dt>
      <dd className="md:col-span-2 text-sm text-slate-900">{value}</dd>
    </div>
  );
}

export default function PublicReportPage() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [photoUrls, setPhotoUrls] = useState({});
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/reports/${token}`)
      .then(({ data }) => { setReport(data); setLoading(false); })
      .catch(() => { setError("Report not found or not available."); setLoading(false); });
  }, [token]);

  useEffect(() => {
    if (!report) return;
    const allIds = [...(report.photos || [])];
    (report.photo_entries || []).forEach(e => { if (e.id) allIds.push(e.id); });
    (report.containers || []).forEach(c => (c.photos || []).forEach(p => allIds.push(p)));
    [...new Set(allIds)].forEach(async pid => {
      try {
        const resp = await axios.get(`${API}/public/photos/${pid}?token=${token}`, { responseType: "blob" });
        setPhotoUrls(prev => ({ ...prev, [pid]: URL.createObjectURL(resp.data) }));
      } catch {}
    });
  }, [report, token]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const resp = await axios.get(`${API}/public/reports/${token}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${report?.report_number || "report"}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {} finally { setDownloading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
      <div className="w-8 h-8 border-3 border-blue-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50">
      <p className="text-slate-500 text-lg">{error}</p>
    </div>
  );

  const rt = report.report_type;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-report-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 md:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://www.cargo-cfs.com/wp-content/uploads/2020/08/prenos.jpg" alt="Cargo CFS" className="h-7" />
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 border-l border-slate-200 pl-3 ml-1">Report</span>
          </div>
          <Button onClick={handleDownload} disabled={downloading} className="bg-blue-900 hover:bg-blue-800 text-white gap-2" data-testid="public-download-pdf">
            <FilePdf size={16} /> {downloading ? "Generating..." : "Download PDF"}
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-2xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>{report.report_number}</h1>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs capitalize">Finalized</Badge>
          </div>
          <p className="text-sm text-slate-500">{REPORT_TYPE_LABELS[rt] || rt}</p>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>General Information</h3>
            <FieldRow label="Report Number" value={report.report_number} />
            <FieldRow label="Applicant" value={report.applicant} />
            <FieldRow label="Reference" value={report.reference_number} />
            <FieldRow label="Customer Ref" value={report.customer_reference_number} />
            <FieldRow label="Date" value={report.date_of_issuance || report.date_of_inspection || report.date_of_operation} />
            <FieldRow label="Surveyor" value={report.surveyor} />
            <FieldRow label="Finalized By" value={report.finalized_by} />
            <FieldRow label="Finalized Date" value={report.finalized_at?.split("T")[0]} />
          </div>

          {/* Damaged Shipment specific */}
          {rt === "damaged_shipment" && (
            <>
              <div className="bg-white border border-slate-200 rounded-md p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Shipment Details</h3>
                <FieldRow label="Shipper" value={report.shipper} />
                <FieldRow label="Consignee" value={report.consignee} />
                <FieldRow label="Carrier" value={report.carrier} />
                <FieldRow label="Container" value={report.container_number} />
                <FieldRow label="Seal" value={report.seal_number} />
                <FieldRow label="B/L / CMR" value={report.bill_of_lading_number} />
                <FieldRow label="Commodity" value={report.commodity_description} />
                <FieldRow label="Weight" value={report.total_gross_weight} />
              </div>
              <div className="bg-white border border-slate-200 rounded-md p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Damage Details</h3>
                <FieldRow label="Damage Type" value={(report.damage_type || "").replace(/_/g, " ")} />
                <FieldRow label="Severity" value={report.damage_severity} />
                <FieldRow label="Description" value={report.description_of_damage} />
                <FieldRow label="Suspected Cause" value={report.suspected_cause} />
                <FieldRow label="Action Taken" value={report.immediate_action_taken} />
              </div>
            </>
          )}

          {/* Transloading specific */}
          {rt === "transloading" && (
            <>
              <div className="bg-white border border-slate-200 rounded-md p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Operation Details</h3>
                <FieldRow label="Cargo" value={report.cargo_description} />
                <FieldRow label="Origin Container" value={report.origin_container} />
                <FieldRow label="Destination Container" value={report.destination_container} />
                <FieldRow label="Origin Seal" value={report.origin_seal} />
                <FieldRow label="New Seal" value={report.new_seal} />
                <FieldRow label="Equipment" value={report.equipment_used} />
              </div>
              {(report.operation_timeline || []).length > 0 && (
                <div className="bg-white border border-slate-200 rounded-md p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Timeline</h3>
                  <table className="w-full text-sm">
                    <thead><tr className="bg-slate-50 border-b"><th className="text-left px-3 py-2 text-xs font-semibold uppercase text-slate-500">Time</th><th className="text-left px-3 py-2 text-xs font-semibold uppercase text-slate-500">Activity</th><th className="text-left px-3 py-2 text-xs font-semibold uppercase text-slate-500">Person</th></tr></thead>
                    <tbody>{report.operation_timeline.map((r, i) => (
                      <tr key={i} className="border-b border-slate-100"><td className="px-3 py-2">{r.time}</td><td className="px-3 py-2">{r.activity}</td><td className="px-3 py-2">{r.responsible_person}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* Findings */}
          {(report.survey_findings || report.observations || report.recommendation) && (
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Findings</h3>
              <FieldRow label="Findings" value={report.survey_findings} />
              <FieldRow label="Observations" value={report.observations} />
              <FieldRow label="Exceptions" value={report.exceptions} />
              <FieldRow label="Recommendation" value={report.recommendation} />
            </div>
          )}

          {/* Disclaimer */}
          {report.disclaimer && (
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Disclaimer</h3>
              <p className="text-sm text-slate-600">{report.disclaimer}</p>
            </div>
          )}

          {/* Photos */}
          {((report.photo_entries || []).length > 0 || (report.photos || []).length > 0) && (
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Photos</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(report.photo_entries || []).map((entry, i) => (
                  <div key={i} className="border border-slate-200 rounded overflow-hidden">
                    <div className="aspect-video bg-slate-100">
                      {photoUrls[entry.id] ? <img src={photoUrls[entry.id]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-400">Loading...</div>}
                    </div>
                    <div className="p-2 bg-slate-50">
                      <p className="text-sm font-medium text-slate-700">{entry.caption || `Photo ${i+1}`}</p>
                      {entry.category && <p className="text-xs text-slate-500">{entry.category}</p>}
                    </div>
                  </div>
                ))}
                {(report.photos || []).map((pid, i) => (
                  <div key={pid} className="aspect-video bg-slate-100 rounded overflow-hidden border border-slate-200">
                    {photoUrls[pid] ? <img src={photoUrls[pid]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-400">Loading...</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 pt-6 border-t border-slate-200 flex items-center gap-2 text-xs text-slate-400">
          <Anchor size={14} /> CARGO CFS d.o.o. | Ankaranska cesta 5, 6000 Koper, Slovenia
        </div>
      </main>
    </div>
  );
}
